// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { Action } from "redux"
import { CreateTagKeywordRequest, GetTagRequest } from "generated/legebill/site/tag/v4/tag_pb"
import * as Actions from "../actions"
import * as Slice from "../slice"
import { TagKeyword } from "generated/legebill/type/tag_pb"
import { tagServiceClient } from "app/rpc/client"
import { grpc } from "app/rpc/executor"
import { never } from "grpc-web-rx"
import { StatusCode } from "grpc-web"
import { isGrpcError } from "../../../lib/rpcutils"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"

type CreateTagKeyword = (request: CreateTagKeywordRequest) => Observable<TagKeyword>

export const createTagKeyword = (createTagKeywordRpc: CreateTagKeyword) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.createTagKeyword.match),
      mergeMap(({ payload }) => of(payload)
        .pipe(
          mergeMap(createTagKeywordRpc),
          map(t => t.toObject()),
          map(Slice.createTagKeywordFulfilled),
          catchError(e => handleError(e, payload.getKeyword())),
        )
      )
    )

const createTagKeywordRpc = (request: CreateTagKeywordRequest): Observable<TagKeyword> =>
  grpc(() => tagServiceClient.createTagKeyword(request, null), never)

const handleError = (e: unknown, tagKeyword: TagKeyword | undefined): Observable<Action> => {
  if (isGrpcError(e) && tagKeyword !== undefined) {
    const { code } = e
    if (code === StatusCode.ALREADY_EXISTS) {
      // Already exists, fetch the tag again as the frontend might be out-of-sync
      return of(tagKeyword)
        .pipe(
          map(k => Actions.getTag({
            request: new GetTagRequest().setTagId(k.getTagId())
          })),
        )
    }
    if (code === StatusCode.NOT_FOUND) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_get_tag_not_found", message: "Tag not found. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
          messageType: "ERROR",
        })),
        of(Slice.setDialogStatus("CLOSED")),
        of(Slice.removeFromCache(tagKeyword.getTagId()))
      )
    }
    if (code === StatusCode.PERMISSION_DENIED) {
      return of(showSnackBar({
        message: t({ id: "msg_create_tag_keyword_permission_denied", message: "Failed to add keyword. You do not have permission to edit tags." }),
        messageType: "ERROR"
      }))
    }
  }

  return of(showSnackBar({
    message: t({ id: "msg_create_tag_keyword_failed", message: "Failed to add keyword. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}

export default createTagKeyword(createTagKeywordRpc)
